import { useContext, useState } from "react";
import {
  displayCaseClientStatus,
  displayCaseOutcomeStatus,
  displayDateString,
  displayReviewStatus,
  formatDateBackend,
  getCaseLockInfo,
  parseDate,
} from "Backend";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import theme, { DataPendoIconButtonProps, EllipsisSX } from "theme";
import SkeletonRow from "components/SkeletonRow";
import SimpleMenu, { SimpleMenuItem } from "components/SimpleMenu";
import EditIcon from "@mui/icons-material/Edit";
import { useAppSelector } from "redux/hooks";
import { selectIsSupport } from "redux/slices/session";
import { deleteClientLensCase, selectCasePropertySchemas } from "redux/slices/clientLens";
import { Delete } from "@mui/icons-material";
import { Case, ClientLens, CustomCaseProperty, NamedUser } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { P } from "theme/typography";
import CaseEditInfoDialogWrapper from "../case/CaseInfoEditWrapper";
import { FullName } from "components/FullName";
import { CaseIdUpdateDialog } from "components/CaseIdUpdateDialog";
import { AppRoutes } from "AppRoutes";

const CaseRow = (props: {
  case: Case;
  actions: SimpleMenuItem[];
  isDefenseFirm?: boolean;
  caseProperties: CustomCaseProperty[];
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  const caseLockedInfo = getCaseLockInfo(props.caseProperties, props.case);
  const c = props.case;
  const info = c.info;
  const caseRoute = AppRoutes.caseDetailBuilder(c.lensId, c.id, "");

  return (
    <TableRow hover key={c.id} selected={false}>
      {!isMobile && !caseLockedInfo.locked ? (
        <TableCell align="left">
          <SimpleMenu hamburger items={props.actions} dataPendo="case-table-row-action-button" />
        </TableCell>
      ) : !isMobile && caseLockedInfo.locked ? (
        <TableCell />
      ) : (
        <></>
      )}
      {caseLockedInfo.locked && caseLockedInfo.unlockDate ? (
        <Tooltip title={`Unlock date: ${formatDateBackend(parseDate(caseLockedInfo.unlockDate))}`} arrow>
          <TableCell sx={EllipsisSX}>{info.clientCaseId}</TableCell>
        </Tooltip>
      ) : (
        <TableCell
          data-pendo="case-table-row-case-id"
          padding="none"
          sx={{
            ...theme.mixins.linkLike,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={info.clientCaseId}>
          <a href={caseRoute} title={info.clientCaseId} className="block w-full p-4">
            {info.clientCaseId}
          </a>
        </TableCell>
      )}
      <TableCell
        sx={{
          overflow: { xs: "hidden" },
          textOverflow: { xs: "ellipsis" },
          whiteSpace: { xs: "nowrap" },
        }}>
        {info.claimantGivenName} {info.claimantSurname}
      </TableCell>
      <TableCell>{info.dateOfBirth ? displayDateString(info.dateOfBirth) : ""}</TableCell>
      {!isMobile && (
        <>
          <TableCell>{displayCaseClientStatus(c.clientStatus)}</TableCell>
          <TableCell>{displayReviewStatus(c.reviewStatus)}</TableCell>
          <TableCell>{c.outcomeStatus && displayCaseOutcomeStatus(c.outcomeStatus)}</TableCell>
          <TableCell>
            <FullName userId={c.userAssignee?.id} defaultName="" />
          </TableCell>
          <TableCell sx={{ textAlign: "center" }}>{c.documentCount > 0 && c.documentCount}</TableCell>
          {props.isDefenseFirm && (
            <TableCell title={c.plaintiffFirm?.name} sx={EllipsisSX}>
              {c.plaintiffFirm?.name}
            </TableCell>
          )}
        </>
      )}
    </TableRow>
  );
};

export interface CaseTableProps {
  clientLens: ClientLens;
  cases: Case[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
  loading: boolean;
  caseAssignees: NamedUser[];
  refreshCases: () => void;
  showFirms?: boolean;
}
const CaseTable = (props: CaseTableProps) => {
  const { updateParams } = useSearchParamsWithUpdate();
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [editOpen, setEditOpen] = useState(false);
  const [editCaseIdOpen, setEditCaseIdOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const isSupport = useAppSelector(selectIsSupport);
  const snackpatch = useAppDispatchWithSnackbar();
  const [selectedCase, setSelectedCase] = useState<Case | undefined>(undefined);
  const caseProperties = useAppSelector(selectCasePropertySchemas);

  const actionItems = (ccase: Case) => {
    const items: SimpleMenuItem[] = [
      {
        name: "Edit",
        disabled: false,
        dataPendo: "case-table-row-edit-button",
        icon: <EditIcon />,
        action: () => {
          setSelectedCase(ccase);
          setEditOpen(true);
        },
      },
    ];

    if (isSupport) {
      items.push({
        name: "Update Case ID",
        disabled: false,
        dataPendo: "case-table-row-case-id-edit-button",
        icon: <EditIcon />,
        action: () => {
          setSelectedCase(ccase);
          setEditCaseIdOpen(true);
        },
      });
    }

    if (isSupport) {
      items.push({
        name: "Delete",
        disabled: false,
        dataPendo: "case-table-row-delete-button",
        icon: <Delete />,
        action: () => {
          confirmCtx.confirm({
            title: "Delete Case",
            prompt: (
              <>
                <P variant="body2">Are you sure you want to delete the following case?</P>
                <P>
                  {ccase.info.claimantGivenName} {ccase.info.claimantSurname} ({ccase.info.clientCaseId})
                </P>
              </>
            ),
            confirmButtonText: "Delete",
            callback: () => {
              return snackpatch(deleteClientLensCase({ caseId: ccase.id }), {
                successMessage: "Case deleted",
                errorMessage: "Unable to delete case",
              });
            },
          });
        },
      });
    }
    return items;
  };

  const rows = () => {
    if (props.loading) {
      return Array(8)
        .fill(0)
        .map((_x, idx) => <SkeletonRow key={idx} columnNum={props.showFirms ? 9 : 8} />);
    } else {
      return props.cases.map((ccase, idx) => {
        return (
          <CaseRow
            caseProperties={caseProperties}
            key={idx}
            case={ccase}
            actions={actionItems(ccase)}
            isDefenseFirm={props.showFirms}
          />
        );
      });
    }
  };

  return (
    <>
      <TableContainer>
        <Table stickyHeader size="small" sx={{ tableLayout: { xs: "fixed" } }}>
          <TableHead>
            <TableRow>
              {!isMobile && <TableCell>Action</TableCell>}
              <TableCell>Case ID</TableCell>
              <TableCell>Claimant Name</TableCell>
              <TableCell>Date of Birth</TableCell>
              {!isMobile && (
                <>
                  <TableCell>Case Status</TableCell>
                  <TableCell>Review Status</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>Documents</TableCell>
                  {props.showFirms && <TableCell>Firm</TableCell>}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>{rows()}</TableBody>
        </Table>
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TablePagination
              sx={{
                ".MuiTablePagination-spacer": {
                  flex: 0,
                },
              }}
              count={props.pagination.total}
              rowsPerPage={props.pagination.limit}
              page={props.pagination.page}
              onPageChange={(_evt, page) =>
                updateParams([
                  {
                    key: "page",
                    value: `${page + 1}`,
                  },
                ])
              }
              onRowsPerPageChange={(evt) =>
                updateParams([
                  {
                    key: "rowsPerPage",
                    value: evt.target.value,
                  },
                  { key: "page", value: `1` },
                ])
              }
              slotProps={{
                actions: {
                  nextButton: {
                    "data-pendo": "case-table-pagination-next",
                  } as DataPendoIconButtonProps,
                  previousButton: {
                    "data-pendo": "case-table-pagination-previous",
                  } as DataPendoIconButtonProps,
                },
              }}
            />
          </TableRow>
        </TableBody>
      </Table>

      {selectedCase && editOpen && (
        <CaseEditInfoDialogWrapper
          ccase={selectedCase}
          caseAssignees={props.caseAssignees}
          onClose={() => setEditOpen(false)}
          open={editOpen}
        />
      )}
      {selectedCase && (
        <CaseIdUpdateDialog mycase={selectedCase} open={editCaseIdOpen} onClose={() => setEditCaseIdOpen(false)} />
      )}
    </>
  );
};

export default CaseTable;
